import { useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, AlertCircle } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";

import ProfileSidebar from "./ProfileSidebar";
import ProfileForm from "./ProfileForm";
import SecurityForm from "./SecurityForm";
import Skeleton from "../../components/Skeleton";

import "../../styles/profile-page.css";

interface ProfilePageProps {
  section?: "profile" | "security";
}

export default function ProfilePage({ section = "profile" }: ProfilePageProps) {
  const { user, loading, refreshUser } = useAuth();
  const [completionPct, setCompletionPct] = useState(50);
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    await refreshUser();
    setRetrying(false);
  };

  // Loading skeleton state
  if (loading || retrying) {
    return (
      <div className="profile-page-container">
        <div className="profile-breadcrumb">
          <span>Settings</span> / <span>Loading...</span>
        </div>
        <div className="profile-page-title">Profile Settings</div>
        <div className="profile-layout-grid">
          <div style={{ height: 320, borderRadius: 12, background: "rgba(255, 255, 255, 0.04)" }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ height: 100, borderRadius: 12, background: "rgba(255, 255, 255, 0.04)" }} />
            <div style={{ height: 380, borderRadius: 12, background: "rgba(255, 255, 255, 0.04)" }} />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (!user) {
    return (
      <div className="profile-page-container">
        <div
          style={{
            padding: "32px",
            borderRadius: 12,
            background: "rgba(239, 68, 68, 0.08)",
            border: "1px solid rgba(239, 68, 68, 0.25)",
            color: "#ef4444",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            textAlign: "center",
            marginTop: 40,
          }}
        >
          <AlertCircle size={36} />
          <div>
            <h3 style={{ fontSize: "1.1rem", fontWeight: 700, margin: 0 }}>
              Unable to load account profile
            </h3>
            <p style={{ fontSize: "0.85rem", opacity: 0.9, marginTop: 4, margin: 0 }}>
              Could not retrieve user session details from the server.
            </p>
          </div>
          <button
            onClick={handleRetry}
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              background: "#ef4444",
              color: "#ffffff",
              border: "none",
              cursor: "pointer",
              fontWeight: 700,
              fontSize: "0.85rem",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  const isProfileTab = section === "profile";

  return (
    <div className="profile-page-container">
      {/* BREADCRUMB */}
      <div className="profile-breadcrumb">
        <span>Settings</span> / <span style={{ color: "var(--dash-text-primary, #f8fafc)" }}>{isProfileTab ? "Profile" : "Security"}</span>
      </div>

      {/* PAGE TITLE */}
      <h1 className="profile-page-title">{isProfileTab ? "Profile" : "Security"}</h1>

      {/* TWO COLUMN GRID LAYOUT */}
      <div className="profile-layout-grid">
        {/* LEFT SETTINGS SIDEBAR NAV */}
        <ProfileSidebar currentSection={section} completionPct={completionPct} />

        {/* RIGHT CONTENT PANEL */}
        <div className="profile-content-panel">
          {isProfileTab ? (
            <ProfileForm user={user} onCompletionChange={setCompletionPct} />
          ) : (
            <SecurityForm />
          )}
        </div>
      </div>
    </div>
  );
}
